import {useCallback, useEffect, useRef, useState} from "react";
import {productPassportData} from "../utils/nbcUtils";

const myMessageClasses = 'bg-[#efefef] border border-white px-3 py-1 rounded-[6px] ml-auto';

declare global {
    interface Window {
        webkitSpeechRecognition: any;
        SpeechRecognition: any;
    }
}

const Nbc = () => {
    const [listening, setListening] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [messages, setMessages] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const messagesRef = useRef<HTMLDivElement | null>(null);
    const recognition = useRef(new window.webkitSpeechRecognition());

    useEffect(() => {
        if (messagesRef?.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [messages]);

    const textToSpeech = useCallback((text: string) => {
        const utterance = new SpeechSynthesisUtterance(text);

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
    }, []);

    const stopTextToSpeech = useCallback(() => {
        window.speechSynthesis.cancel();
    }, []);

    const sendFunction = useCallback(async (question: string, fromSpeech: boolean) => {
        try {
            setLoading(true);
            const response = await fetch('https://jlutv77h7i.execute-api.eu-west-1.amazonaws.com/ask', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question,
                    passport_data: productPassportData,
                }),
            });

            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }

            const result = await response.json();

            if (fromSpeech) {
                textToSpeech(result.answer);
            }
            setMessages((prevState) => [...prevState, result.answer]);
            setLoading(false);
        } catch (error: any) {
            const mockResponse = {
                "answer": 'an error appeared',
                "on_topic": true
            }
            if (fromSpeech) {
                textToSpeech(mockResponse.answer);
            }
            setMessages((prevState) => [...prevState, mockResponse.answer]);
            setLoading(false);
        }
    }, [inputValue]);

    const inputSubmit = useCallback((value: string, fromSpeech = false) => {
        if (!value.trim() || loading) return;

        setMessages((prevState) => [...prevState, value]);
        setInputValue("");
        sendFunction(value, fromSpeech);
    }, [loading, sendFunction]);

    const startListening = useCallback(() => {
        recognition.current.onresult = (event: any) => {
            setListening(false);
            const text = event.results[0][0].transcript;
            inputSubmit(`${inputValue.trim()} ${text.trim()}`, true);
        };

        setListening(true);
        recognition.current.start();
    }, [inputValue, loading]);

    const stopListening = useCallback(() => {
        recognition.current.stop();
        setListening(false);
    }, []);

    const onEnterPress = useCallback((e: any) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            inputSubmit(inputValue);
        }
    }, [inputSubmit, inputValue]);

    return (
        <>
            <div>
                <div className="max-w-[480px] mx-auto bg-[#f1eee6] pt-5 px-2.5 pb-1">
                    <div ref={messagesRef} className='flex flex-col max-h-[250px] overflow-auto gap-2.5'>
                        {messages.map((message, index) => (
                            <div className={`${index % 2 ? 'mr-auto' : myMessageClasses} flex gap-2`} key={index}>
                                {message}
                                {!!(index % 2) && !isSpeaking && <img src="/images/icons/sound.svg" alt="sound" className="w-4 cursor-pointer" onClick={() => textToSpeech(message)} />}
                                {!!(index % 2) && isSpeaking && <img src="/images/icons/stop.svg" alt="sound" className="w-4 cursor-pointer" onClick={() => stopTextToSpeech()} />}
                            </div>
                        ))}
                        {!messages.length && (
                            <div>
                                <img src='/images/icons/stars.svg' alt='stars' className="w-10 mx-auto" />
                                <div className="text-center">Ask me about this dress</div>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-between pt-2.5">
                        <div className="w-full">
                            <div className="relative">
                                <input
                                    placeholder=""
                                    className="w-full py-1 pl-2 pr-16 text-sm text-gray-900 bg-white border rounded-xl outline-none transition-all duration-200 border-gray-300 focus:border-gray-400 focus:ring-2 focus:ring-gray-200"
                                    value={inputValue}
                                    onChange={(event) => setInputValue(event.target.value)}
                                    onKeyDown={onEnterPress}
                                />

                                {listening ?
                                    <div className="absolute right-10 top-1/2 -translate-y-1/2 w-[34px] h-[30px] flex items-center justify-center cursor-pointer" onClick={stopListening}>
                                        <img src="/images/icons/microphone-red.svg" alt="Microphone" className="w-5" />
                                    </div> :
                                    <div
                                        className="absolute right-10 top-1/2 -translate-y-1/2 w-[34px] h-[30px] flex items-center justify-center cursor-pointer"
                                        onClick={() => startListening()}
                                    >
                                        <img src="/images/icons/microphone.svg" alt="Microphone" className="w-5" />
                                    </div>
                                }

                                {loading ?
                                    <div className="absolute right-1.5 top-1/2 -translate-y-1/2 w-[34px] h-[30px] flex items-center justify-center">
                                        <div className="w-4 h-4 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div>
                                    </div> :
                                    <div className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-[34px] h-[30px] flex items-center justify-center ${loading ? 'cursor-default' : 'cursor-pointer'}`} onClick={() => inputSubmit(inputValue)}>
                                        <img src="/images/icons/send.svg" alt="send" className="w-5" />
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <iframe
                src="https://nobodyschild.xela.co/dl/01/5056825413483"
                style={{width: "100%", height: "100vh", border: "none"}}
            />
        </>
    );
}

export default Nbc;
